import type { LosiGameId, LosiGameRankWhisper, LosiGameScore } from './types'

const STORAGE_KEY = 'losi-games:scores:v1'

/** Seeded field so early players still get a private position among "everyone". */
const FIELD_SIZE: Record<LosiGameId, number> = {
  'ping-pong': 840,
  'eye-break': 880,
  'eye-snake': 910,
  'eye-flap': 950,
  'eye-match': 720,
  'space-shooter': 920,
  asteroids: 870,
  pinball: 760,
  vibecode: 410
}

function readScores(): LosiGameScore[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as LosiGameScore[]
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function writeScores(scores: LosiGameScore[]) {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(scores.slice(-400)))
}

export function bestLocalScore(gameId: LosiGameId): number {
  return readScores()
    .filter((s) => s.gameId === gameId)
    .reduce((best, s) => Math.max(best, s.score), 0)
}

/**
 * Record a score and whisper rank. Tracks local high score for every game.
 */
export function recordScoreAndWhisper(
  gameId: LosiGameId,
  score: number,
  userId?: string | null
): LosiGameRankWhisper {
  const prevBest = bestLocalScore(gameId)
  const entry: LosiGameScore = { gameId, score, at: Date.now(), userId }
  const all = readScores()
  all.push(entry)
  writeScores(all)

  const peers = all.filter((s) => s.gameId === gameId).map((s) => s.score)
  const field = FIELD_SIZE[gameId]
  const maxPeer = Math.max(1, ...peers, score)
  const quality = Math.min(1, Math.max(0, score / maxPeer))
  const rank = Math.max(1, Math.round(field - quality * (field - 1) * 0.92))
  const newBest = score > prevBest
  return {
    gameId,
    score,
    rank,
    of: field,
    newBest,
    label: newBest
      ? `New high score ${score} — you're #${rank} of ${field} (board stays quiet).`
      : `Score ${score}. Best ${Math.max(prevBest, score)}. You're #${rank} of ${field}.`
  }
}
